import { defineMessages, MessageDescriptor } from 'react-intl';
import { SocialPostType } from '../promote/types';

/**
 * The copy of the Social screen (design social-connectors b and c, your-intelligence g). The ids
 * keep the old `promote.performance` prefix: Social is the Performance screen with a new name.
 */
export const socialMessages = defineMessages({
  title: { id: 'app.sml.social.title', defaultMessage: 'Social' },
  intro: {
    id: 'app.sml.social.intro',
    defaultMessage:
      'Your followers and every post on your channels, and what each post moved. MUSIC24 reads the numbers once a day.',
  },
  periodDates: { id: 'app.sml.social.periodDates', defaultMessage: '{start} – {end}' },
  reconnectWarning: {
    id: 'app.sml.social.reconnectWarning',
    defaultMessage: '{channel} is signed out. Its numbers stop at {date}.',
  },
  // The native apps read the Channels id; the web links to Settings → Connections (navigation v2).
  reconnectInChannels: { id: 'app.sml.social.reconnectInChannels', defaultMessage: 'Reconnect in Channels' },
  reconnectInConnections: { id: 'app.sml.social.reconnectInConnections', defaultMessage: 'Reconnect in Connections' },
  signedOutLine: { id: 'app.sml.social.signedOutLine', defaultMessage: 'Signed out. Numbers stop at {date}.' },
  readScopeLine: {
    id: 'app.sml.social.readScopeLineConnections',
    defaultMessage: 'MUSIC24 cannot read the numbers. Reconnect in Connections.',
  },
  // The native apps read the old Channels copy (navigation v2 renamed the page Connections).
  readScopeLineNative: {
    id: 'app.sml.social.readScopeLine',
    defaultMessage: 'MUSIC24 cannot read the numbers. Reconnect in Channels.',
  },
  periodLabel: { id: 'app.sml.promote.performance.period.label', defaultMessage: 'Period' },
  periodDays: { id: 'app.sml.promote.performance.period.days', defaultMessage: '{days}d' },
  upgradeChip: { id: 'app.sml.lockChip.needsPlan', defaultMessage: 'Needs the {plan} plan' },
  upgradeTitle: {
    id: 'app.sml.promote.performance.upgrade.title',
    defaultMessage: 'See {days} days of your posts',
  },
  upgradeText: {
    id: 'app.sml.promote.performance.upgrade.text',
    defaultMessage:
      '{currentPlan} keeps {currentDays} days of history. {plan} keeps {days} days of followers and post numbers, and MUSIC24 has them ready the moment you upgrade.',
  },
  upgradeButton: { id: 'app.sml.promote.performance.upgrade.button', defaultMessage: 'See plans' },
  views: { id: 'app.sml.promote.performance.views', defaultMessage: 'Views' },
  likes: { id: 'app.sml.promote.performance.likes', defaultMessage: 'Likes' },
  comments: { id: 'app.sml.promote.performance.comments', defaultMessage: 'Comments' },
  commentsShort: { id: 'app.sml.promote.performance.comments.short', defaultMessage: 'Comm.' },
  shares: { id: 'app.sml.promote.performance.shares', defaultMessage: 'Shares' },
  saves: { id: 'app.sml.promote.performance.saves', defaultMessage: 'Saves' },
  reach: { id: 'app.sml.promote.performance.reach', defaultMessage: 'Reach' },
  // Only X gives the link clicks of a post (design x-connector e). Null elsewhere: no tile, no column.
  linkClicks: { id: 'app.sml.social.linkClicks', defaultMessage: 'Link clicks' },
  linkClicksShort: { id: 'app.sml.social.linkClicksShort', defaultMessage: 'Clicks' },
  engagement: { id: 'app.sml.promote.performance.engagement', defaultMessage: 'Engagement' },
  postsTitle: { id: 'app.sml.promote.performance.posts.title', defaultMessage: 'Posts' },
  postsCount: {
    id: 'app.sml.promote.performance.posts.count',
    defaultMessage: '{count, plural, one {# post} other {# posts}}',
  },
  postsCountMore: {
    id: 'app.sml.promote.performance.posts.countMore',
    defaultMessage: '{count}+ posts',
  },
  filterChannel: { id: 'app.sml.promote.performance.filter.channel', defaultMessage: 'Channel' },
  filterAllChannels: { id: 'app.sml.promote.performance.filter.allChannels', defaultMessage: 'All channels' },
  filterType: { id: 'app.sml.promote.performance.filter.type', defaultMessage: 'Type' },
  filterAllTypes: { id: 'app.sml.promote.performance.filter.allTypes', defaultMessage: 'All types' },
  filterOurs: { id: 'app.sml.promote.performance.filter.ours', defaultMessage: 'Published with MUSIC24' },
  columnPost: { id: 'app.sml.promote.performance.column.post', defaultMessage: 'Post' },
  columnType: { id: 'app.sml.promote.performance.column.type', defaultMessage: 'Type' },
  columnDate: { id: 'app.sml.promote.performance.column.date', defaultMessage: 'Date' },
  noCaption: { id: 'app.sml.promote.performance.post.noCaption', defaultMessage: 'No caption' },
  footerMissing: {
    id: 'app.sml.promote.performance.footer.missing',
    defaultMessage: 'The platform does not give this number.',
  },
  loadMore: { id: 'app.sml.promote.performance.loadMore', defaultMessage: 'Load more' },
  phoneViews: { id: 'app.sml.promote.performance.phone.views', defaultMessage: '{count} views' },
  phoneLikes: { id: 'app.sml.promote.performance.phone.likes', defaultMessage: '{count} likes' },
  phoneComments: { id: 'app.sml.promote.performance.phone.comments', defaultMessage: '{count} comments' },
  noChannelTitle: {
    id: 'app.sml.social.empty.noChannel.title',
    defaultMessage: 'No channel connected yet',
  },
  noChannelText: {
    id: 'app.sml.social.empty.noChannel.textConnections',
    defaultMessage:
      'Connect TikTok, Instagram, YouTube, Facebook, Threads or X in Connections. MUSIC24 then reads your followers and every post, once a day.',
  },
  // The native apps read the old Channels copy (navigation v2 renamed the page Connections).
  noChannelTextNative: {
    id: 'app.sml.social.empty.noChannel.text',
    defaultMessage:
      'Connect TikTok, Instagram, YouTube, Facebook, Threads or X in Channels. MUSIC24 then reads your followers and every post, once a day.',
  },
  noChannelButton: {
    id: 'app.sml.social.empty.noChannel.buttonConnections',
    defaultMessage: 'Connect a channel in Connections',
  },
  // The native apps read the old Channels copy (navigation v2 renamed the page Connections).
  noChannelButtonNative: {
    id: 'app.sml.social.empty.noChannel.button',
    defaultMessage: 'Connect a channel in Channels',
  },
  noPostsTitle: {
    id: 'app.sml.promote.performance.empty.noPosts.title',
    defaultMessage: 'No posts in this period',
  },
  noPostsText: {
    id: 'app.sml.promote.performance.empty.noPosts.text',
    defaultMessage: 'Pick a longer period or clear the filters.',
  },
  noPostsButton: { id: 'app.sml.promote.performance.empty.noPosts.button', defaultMessage: 'Clear filters' },
  detailTitle: { id: 'app.sml.promote.performance.detail.title', defaultMessage: 'Post' },
  detailClose: { id: 'app.sml.promote.performance.detail.close', defaultMessage: 'Close' },
  detailOpenOn: { id: 'app.sml.promote.performance.detail.openOn', defaultMessage: 'Open on {platform}' },
  detailOpenCalendar: {
    id: 'app.sml.promote.performance.detail.openCalendar',
    defaultMessage: 'Open in Calendar',
  },
  detailMissing: {
    id: 'app.sml.promote.performance.detail.missing',
    defaultMessage: '{platform} does not give this number.',
  },
  detailChartTitle: { id: 'app.sml.promote.performance.detail.chartTitle', defaultMessage: '{metric} per day' },
  detailMetric: { id: 'app.sml.promote.performance.detail.metric', defaultMessage: 'Number of the daily line' },
  detailOneRead: {
    id: 'app.sml.promote.performance.detail.oneRead',
    defaultMessage: 'The daily line starts after the second daily read.',
  },
  detailLastRead: {
    id: 'app.sml.promote.performance.detail.lastRead',
    defaultMessage: 'MUSIC24 reads this post once a day. Last read {date}, {time}.',
  },
  detailStory: {
    id: 'app.sml.promote.performance.detail.story',
    defaultMessage: 'A story is gone after 24 hours. These are its last numbers.',
  },
});

const typeMessages = defineMessages({
  reel: { id: 'app.sml.promote.performance.type.reel', defaultMessage: 'Reel' },
  short: { id: 'app.sml.promote.performance.type.short', defaultMessage: 'Short' },
  video: { id: 'app.sml.promote.performance.type.video', defaultMessage: 'Video' },
  photo: { id: 'app.sml.promote.performance.type.photo', defaultMessage: 'Photo' },
  carousel: { id: 'app.sml.promote.performance.type.carousel', defaultMessage: 'Carousel' },
  text: { id: 'app.sml.promote.performance.type.text', defaultMessage: 'Text' },
  story: { id: 'app.sml.promote.performance.type.story', defaultMessage: 'Story' },
});

/** The label of each type badge. "Reel" and "Short" keep the capital letter. */
export const socialPostTypeMessages: Record<SocialPostType, MessageDescriptor> = typeMessages;

/** The mark of a post that Music24 published. A brand name: it is not translated. */
export const MUSIC24_MARK = 'MUSIC24';
